import type { FemaAlertArea, FemaCoordinate } from 'types/fema.types';

/**
 * The most decimal places a CAP area coordinate carries. FEMA advises against more than 4
 * (IPAWS-OPEN IDG v4.06 §8.9, p85; advisory 627). The NWEM 3-decimal rule is separate and
 * stays in `generateFemaAreaGeometry`.
 */
export const CAP_COORDINATE_DECIMAL_PLACES = 4;

const CAP_COORDINATE_SCALE = 10 ** CAP_COORDINATE_DECIMAL_PLACES;

/**
 * Truncates a latitude or longitude toward zero to `CAP_COORDINATE_DECIMAL_PLACES`. It never
 * rounds: -97.123456 becomes -97.1234. The `toFixed(6)` step removes binary floating-point
 * noise from the scaled value (38.1234 * 10000 is 381233.99999999994 in binary). A value that
 * is not finite stays as it is, so validation still reports it.
 */
export const truncateCapCoordinate = (value: number): number =>
  Number.isFinite(value)
    ? Math.trunc(Number((value * CAP_COORDINATE_SCALE).toFixed(6))) / CAP_COORDINATE_SCALE
    : value;

const truncateFemaCoordinate = ({ latitude, longitude }: FemaCoordinate): FemaCoordinate => ({
  latitude: truncateCapCoordinate(latitude),
  longitude: truncateCapCoordinate(longitude),
});

/** Truncates every polygon vertex and circle center of one CAP area. */
export const truncateAreaGeometryCoordinates = (
  areaGeometry: Pick<FemaAlertArea, 'polygon' | 'circle'>,
): Pick<FemaAlertArea, 'polygon' | 'circle'> => ({
  ...(areaGeometry.polygon
    ? {
        polygon: areaGeometry.polygon.map((polygon) => polygon.map(truncateFemaCoordinate)),
      }
    : {}),
  ...(areaGeometry.circle
    ? {
        circle: areaGeometry.circle.map((circle) => ({
          ...circle,
          center: truncateFemaCoordinate(circle.center),
        })),
      }
    : {}),
});
